import type { FC, PropsWithChildren } from 'react';
import { useEffect, useState, useCallback } from 'react';

import { useAppDispatch } from 'features/app/hooks';
import type { EnvironmentVariablesConfig } from 'features/environment/interfaces';
import {
  EnvironmentDaoService,
  EnvironmentService,
} from 'features/environment/services';
import { handleError } from 'features/notification/store';
import { sipSettings } from 'features/sip/config';

export const EnvironmentConfig: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  const updateSipConfig = useCallback((config: EnvironmentVariablesConfig) => {
    const sipConfig = sipSettings.get();
    const {
      turnServer,
      stunServer,
      stunServerAlt,
      iceServerCredential,
      iceServerUsername,
    } = config.sip;

    const iceServers: RTCIceServer[] = [
      ...(turnServer
        ? [
            {
              urls: [turnServer],
              credential: iceServerCredential,
              username: iceServerUsername,
            },
          ]
        : []),
      ...([stunServer, stunServerAlt].filter(Boolean).length
        ? [
            {
              urls: [stunServer, stunServerAlt].filter(Boolean),
            },
          ]
        : []),
    ];

    sipSettings.update({
      domain: config.sip.domain,
      socket: {
        ...sipConfig.socket,
        uri: config.sip.domain,
      },
      pcConfig: {
        ...sipConfig.pcConfig,
        iceServers,
      },
    });
  }, []);

  useEffect(() => {
    let isMounted = true;
    const setEnvironmentConfig = async () => {
      try {
        const environmentConfig =
          await EnvironmentDaoService.getEnvironmentConfig();
        if (environmentConfig && isMounted) {
          EnvironmentService.setConfig(environmentConfig);
          updateSipConfig(environmentConfig);
        }
      } catch (error) {
        if (isMounted) {
          dispatch(handleError({ error, methodName: 'setEnvironmentConfig' }));
        }
      } finally {
        if (isMounted) {
          setIsConfigLoading(false);
        }
      }
    };
    setEnvironmentConfig();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSipConfig, dispatch]);

  if (isConfigLoading) {
    return <div>Loading configuration...</div>;
  }

  return <>{children}</>;
};
